import { SmartCropMode } from '@/api/generator';

// Constants
const IMAGE_ORIGIN = location.origin;
const IMAGE_BASE_PATH = '/api/images';
export const MOBILE_IMAGE_WIDTH_OVERVIEW = 80;
export const DESKTOP_IMAGE_WIDTH_OVERVIEW = 120;
export const MOBILE_IMAGE_WIDTH_DETAIL = 500;
export const DESKTOP_IMAGE_WIDTH_DETAIL = 250;

// Union types for mutually exclusive image dimension modes
type ImageUrlOptions =
  | { width: number; height: number; aspectRatio?: never; cropMode?: SmartCropMode }
  | { width: number; aspectRatio: number; height?: never; cropMode?: SmartCropMode }
  | { width: number; height?: never; aspectRatio?: never; cropMode?: SmartCropMode };

// Generate image URL with query parameters using mutually exclusive dimension modes
export function getImageUrl(pictureId: string, options: ImageUrlOptions): string {
  const imageUrl = new URL(IMAGE_ORIGIN);
  imageUrl.pathname = `${IMAGE_BASE_PATH}/${pictureId}`;
  imageUrl.searchParams.set('width', Math.round(options.width).toString());

  if ('height' in options && options.height != null) {
    // Explicit height mode: exact pixel dimensions
    imageUrl.searchParams.set('height', Math.round(options.height).toString());
    if (options.cropMode) {
      imageUrl.searchParams.set('smartCrop', options.cropMode.toString());
    }
  } else if ('aspectRatio' in options && options.aspectRatio != null) {
    // Aspect ratio mode: proportional cropping
    imageUrl.searchParams.set('aspectRatio', options.aspectRatio.toString());
    if (options.cropMode) {
      imageUrl.searchParams.set('smartCrop', options.cropMode.toString());
    }
  }
  // Width-only mode: preserve source ratio (no additional params needed)

  return imageUrl.toString();
}

// Generate responsive sizes attribute for images
export function generateSizes(mobileWidth: number, desktopWidth: number): string {
  // Using Vuetify's lg breakpoint (1264px)
  return `(width <= 1264px) ${mobileWidth}px, ${desktopWidth}px`;
}

// Generate responsive srcset attribute for images
export function generateSrcset(
  pictureId: string | null,
  width: number,
  aspectRatio?: number,
  cropMode?: SmartCropMode,
  srcsetSizes: number[] = [1, 2, 3],
): string | undefined {
  if (!pictureId) return undefined;

  return srcsetSizes
    .map((multiplier) => {
      const scaledWidth = Math.round(width * multiplier);

      // Delegate to getImageUrl with appropriate dimension mode
      const url =
        aspectRatio != null
          ? getImageUrl(pictureId, { width: scaledWidth, aspectRatio, cropMode })
          : getImageUrl(pictureId, { width: scaledWidth });

      return `${url} ${scaledWidth}w`;
    })
    .join(', ');
}
